package dev.danko.roblox_craft_ai.uikit.util

import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin
import kotlin.math.pow

enum class EasingStyle {
    Linear, Quad, Sine, Cubic, Bounce, Elastic, Constant
}

enum class EasingDirection {
    In, Out, InOut
}

data class Keyframe(
    val time: Float,
    val boneName: String? = null,
    val poses: Map<String, PoseDto> = emptyMap(),
    val easingStyle: EasingStyle = EasingStyle.Sine,
    val easingDirection: EasingDirection = EasingDirection.InOut
)

data class PoseDto(
    val position: Triple<Double, Double, Double>,
    val rotation: Triple<Double, Double, Double>
)

data class KeyframeSequence(
    val name: String? = null,
    val duration: Float,
    val looped: Boolean = true,
    val keyframes: List<Keyframe>
)

private fun easeOutBounce(x: Double): Double {
    val n1 = 7.5625
    val d1 = 2.75
    return when {
        x < 1.0 / d1 -> n1 * x * x
        x < 2.0 / d1 -> {
            val x2 = x - 1.5 / d1
            n1 * x2 * x2 + 0.75
        }
        x < 2.5 / d1 -> {
            val x2 = x - 2.25 / d1
            n1 * x2 * x2 + 0.9375
        }
        else -> {
            val x2 = x - 2.625 / d1
            n1 * x2 * x2 + 0.984375
        }
    }
}

private fun easeOutElastic(x: Double): Double {
    val c4 = (2.0 * PI) / 3.0
    return when {
        x == 0.0 -> 0.0
        x == 1.0 -> 1.0
        else -> (2.0.pow(-10.0 * x)) * sin((x * 10.0 - 0.75) * c4) + 1.0
    }
}

fun applyEasing(alpha: Double, style: EasingStyle, dir: EasingDirection): Double {
    val a = alpha.coerceIn(0.0, 1.0)
    return when (style) {
        EasingStyle.Linear -> a
        EasingStyle.Constant -> if (a >= 1.0) 1.0 else 0.0
        EasingStyle.Quad -> when (dir) {
            EasingDirection.In -> a * a
            EasingDirection.Out -> 1.0 - (1.0 - a) * (1.0 - a)
            EasingDirection.InOut -> if (a < 0.5) 2.0 * a * a else 1.0 - (-2.0 * a + 2.0).pow(2) / 2.0
        }
        EasingStyle.Sine -> when (dir) {
            EasingDirection.In -> 1.0 - cos((a * PI) / 2.0)
            EasingDirection.Out -> sin((a * PI) / 2.0)
            EasingDirection.InOut -> -(cos(PI * a) - 1.0) / 2.0
        }
        EasingStyle.Cubic -> when (dir) {
            EasingDirection.In -> a * a * a
            EasingDirection.Out -> 1.0 - (1.0 - a).pow(3)
            EasingDirection.InOut -> if (a < 0.5) 4.0 * a * a * a else 1.0 - (-2.0 * a + 2.0).pow(3) / 2.0
        }
        EasingStyle.Bounce -> when (dir) {
            EasingDirection.In -> 1.0 - easeOutBounce(1.0 - a)
            EasingDirection.Out -> easeOutBounce(a)
            EasingDirection.InOut -> if (a < 0.5) (1.0 - easeOutBounce(1.0 - 2.0 * a)) / 2.0 else (1.0 + easeOutBounce(2.0 * a - 1.0)) / 2.0
        }
        EasingStyle.Elastic -> when (dir) {
            EasingDirection.In -> 1.0 - easeOutElastic(1.0 - a)
            EasingDirection.Out -> easeOutElastic(a)
            EasingDirection.InOut -> {
                val c5 = (2.0 * PI) / 4.5
                when {
                    a == 0.0 -> 0.0
                    a == 1.0 -> 1.0
                    a < 0.5 -> -(2.0.pow(20.0 * a - 10.0) * sin((20.0 * a - 11.125) * c5)) / 2.0
                    else -> (2.0.pow(-20.0 * a + 10.0) * sin((20.0 * a - 11.125) * c5)) / 2.0 + 1.0
                }
            }
        }
    }
}

/**
 * Reads keyframes for the preview. The backend's "rc-anim-v1" clips use degrees and named easings
 * ("smooth", "linear", "bounce", "elastic", "constant"); older JSON uses radians and easingStyle/easingDirection.
 */
fun parseRobloxKeyframeSequenceJson(jsonString: String): KeyframeSequence? {
    return try {
        val raw = js("JSON.parse(jsonString)")
        val degrees = (raw.format as? String) == "rc-anim-v1"
        val angleScale = if (degrees) PI / 180.0 else 1.0
        val kfsArray = raw.keyframes
        if (kfsArray == null || js("!Array.isArray(kfsArray)") as Boolean) {
            return null
        }
        val length = (kfsArray.length as? Int) ?: 0
        if (length == 0) return null

        val keyframesList = mutableListOf<Keyframe>()
        var maxTime = 0.0f

        for (i in 0 until length) {
            val kfRaw = kfsArray[i]
            val time = (kfRaw.time as? Number)?.toFloat() ?: continue
            if (time > maxTime) maxTime = time

            val easingStyleStr = kfRaw.easingStyle as? String
            val easingDirStr = kfRaw.easingDirection as? String
            val named = kfRaw.easing as? String
            val style = when (named) {
                "linear" -> EasingStyle.Linear
                "bounce" -> EasingStyle.Bounce
                "elastic" -> EasingStyle.Elastic
                "constant" -> EasingStyle.Constant
                "smooth" -> EasingStyle.Sine
                else -> try {
                    if (easingStyleStr != null) EasingStyle.valueOf(easingStyleStr) else EasingStyle.Sine
                } catch (_: Throwable) { EasingStyle.Sine }
            }
            val dir = when (named) {
                "bounce", "elastic" -> EasingDirection.Out
                null -> try {
                    if (easingDirStr != null) EasingDirection.valueOf(easingDirStr) else EasingDirection.InOut
                } catch (_: Throwable) { EasingDirection.InOut }
                else -> EasingDirection.InOut
            }

            val posesMap = mutableMapOf<String, PoseDto>()
            val posesRaw = kfRaw.poses
            if (posesRaw != null) {
                val keys = js("Object.keys(posesRaw)") as Array<String>
                for (boneName in keys) {
                    val poseRaw = posesRaw[boneName]
                    val posRaw = poseRaw.pos ?: poseRaw.position
                    val rotRaw = poseRaw.rot ?: poseRaw.rotation

                    val pos = if (posRaw != null && js("Array.isArray(posRaw)") as Boolean) {
                        val px = (posRaw[0] as? Number)?.toDouble() ?: 0.0
                        val py = (posRaw[1] as? Number)?.toDouble() ?: 0.0
                        val pz = (posRaw[2] as? Number)?.toDouble() ?: 0.0
                        Triple(px, py, pz)
                    } else Triple(0.0, 0.0, 0.0)

                    val rot = if (rotRaw != null && js("Array.isArray(rotRaw)") as Boolean) {
                        val rx = ((rotRaw[0] as? Number)?.toDouble() ?: 0.0) * angleScale
                        val ry = ((rotRaw[1] as? Number)?.toDouble() ?: 0.0) * angleScale
                        val rz = ((rotRaw[2] as? Number)?.toDouble() ?: 0.0) * angleScale
                        Triple(rx, ry, rz)
                    } else Triple(0.0, 0.0, 0.0)

                    posesMap[boneName] = PoseDto(pos, rot)
                }
            }

            keyframesList.add(Keyframe(
                time = time,
                poses = posesMap,
                easingStyle = style,
                easingDirection = dir
            ))
        }

        val seqName = raw.name as? String
        val loopedRaw = raw.looped
        val looped = if (loopedRaw is Boolean) loopedRaw else true
        val rawDuration = (raw.duration as? Number)?.toFloat()
        val finalDuration = rawDuration ?: maxTime

        KeyframeSequence(
            name = seqName,
            duration = finalDuration,
            looped = looped,
            keyframes = keyframesList.sortedBy { it.time }
        )
    } catch (e: dynamic) {
        js("console.error('[KeyframeSequenceParser] parse error', e)")
        null
    }
}

data class SurroundingKeyframes(
    val prev: Keyframe?,
    val next: Keyframe?
)

fun findSurroundingKeyframes(
    boneName: String,
    t: Double,
    keyframes: List<Keyframe>
): SurroundingKeyframes {
    val relevant = keyframes.filter { it.poses.containsKey(boneName) }.sortedBy { it.time }
    if (relevant.isEmpty()) return SurroundingKeyframes(null, null)

    var prev: Keyframe? = null
    var next: Keyframe? = null

    for (kf in relevant) {
        if (kf.time.toDouble() <= t) {
            prev = kf
        } else {
            next = kf
            break
        }
    }

    if (prev == null && next == null) {
        prev = relevant.lastOrNull()
        next = relevant.firstOrNull()
    }

    return SurroundingKeyframes(prev, next)
}

fun wrapTime(t: Double, dur: Double, loop: Boolean): Double {
    if (dur <= 0.0) return 0.0
    return if (loop) {
        var wrapped = t % dur
        if (wrapped < 0) wrapped += dur
        wrapped
    } else {
        t.coerceIn(0.0, dur)
    }
}

val sampleWalkCycleJson: String = """
{
    "keyframes": [
        {"time":0.0,"poses":{"Left Leg":{"pos":[-0.5,-1,0],"rot":[0.3,0,0]},"Right Leg":{"pos":[0.5,-1,0],"rot":[-0.3,0,0]},"Left Arm":{"pos":[-1,0.8,0],"rot":[-0.2,0,0]},"Right Arm":{"pos":[1,0.8,0],"rot":[0.2,0,0]},"Torso":{"pos":[0,0,0],"rot":[0,0,0]}}},
        {"time":0.25,"poses":{"Left Leg":{"pos":[-0.5,-1,0],"rot":[0,0,0]},"Right Leg":{"pos":[0.5,-1,0],"rot":[0,0,0]},"Torso":{"pos":[0,0.05,0],"rot":[0,0,0]}},"easingStyle":"Sine","easingDirection":"InOut"},
        {"time":0.5,"poses":{"Left Leg":{"pos":[-0.5,-1,0],"rot":[-0.3,0,0]},"Right Leg":{"pos":[0.5,-1,0],"rot":[0.3,0,0]},"Left Arm":{"pos":[-1,0.8,0],"rot":[0.2,0,0]},"Right Arm":{"pos":[1,0.8,0],"rot":[-0.2,0,0]},"Torso":{"pos":[0,0,0],"rot":[0,0,0]}}},
        {"time":0.75,"poses":{"Left Leg":{"pos":[-0.5,-1,0],"rot":[0,0,0]},"Right Leg":{"pos":[0.5,-1,0],"rot":[0,0,0]},"Torso":{"pos":[0,0.05,0],"rot":[0,0,0]}},"easingStyle":"Sine","easingDirection":"InOut"},
        {"time":1.0,"poses":{"Left Leg":{"pos":[-0.5,-1,0],"rot":[0.3,0,0]},"Right Leg":{"pos":[0.5,-1,0],"rot":[-0.3,0,0]},"Left Arm":{"pos":[-1,0.8,0],"rot":[-0.2,0,0]},"Right Arm":{"pos":[1,0.8,0],"rot":[0.2,0,0]},"Torso":{"pos":[0,0,0],"rot":[0,0,0]}}}
    ],
    "duration": 1.0,
    "looped": true,
    "name": "WalkCycle"
}
"""
